import { BrowserRouter as Router, Routes, Route, useParams, useNavigate, useLocation, Outlet, useOutletContext } from 'react-router-dom'
import './styles/App.css';
import Home from './Home';
import Login from './Login';
import Sidebar from './Sidebar';
import Chatconversation from './Chatconversation';
import PromptGuideModal from './PromptGuideModal';
import Icons from './Icons';
import UserSettings from './UserSettings';
import Status from './Status';
import { useState, useEffect, useCallback } from 'react';
import AuthGuard from './AuthGuard';
import FreeChatGuard from './FreeChatGuard';
import ConversationView from './ConversationView';
import API_CONFIG from './config';

// Persistent Chat Layout Component
function ChatLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [isPromptGuideOpen, setIsPromptGuideOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [copiedText, setCopiedText] = useState('');
  const [currentBotMode, setCurrentBotMode] = useState('knowledge');
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleToggleSidebar = () => {
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    window.dispatchEvent(new CustomEvent('sidebar-toggle', {
      detail: { isOpen: newState }
    }));
  };

  const handleNewChat = () => {
    setCurrentConversation([]);
    setCurrentConversationId(null);
    window.dispatchEvent(new CustomEvent('clearConversation'));
    navigate('/');
  };

  const handleOpenUserSettings = () => {
    setIsUserSettingsOpen(true);
  };

  const handleCloseUserSettings = () => {
    setIsUserSettingsOpen(false);
  };

  const handleSelectConversation = (conversation) => {
    setCurrentConversationId(conversation.id || conversation.conversation_id);
    window.dispatchEvent(new CustomEvent('navigateToConversation', {
      detail: { conversationId: conversation.id || conversation.conversation_id }
    }));
  };

  const handleOpenPromptGuide = useCallback(() => {
    setIsPromptGuideOpen(true);
  }, []);

  const handleClosePromptGuide = useCallback(() => {
    setIsPromptGuideOpen(false);
  }, []);

  const handlePromptSelect = useCallback((promptText) => {
    setUserInput(promptText);
    setCopiedText(promptText);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
    setIsPromptGuideOpen(false);
  }, []);

  const handleStartConversation = useCallback((promptText) => {
    setUserInput(promptText);
    setIsPromptGuideOpen(false);
    // The Chatconversation component will handle the actual sending
    // We just need to set the input and close the modal
  }, []);

  const handleOpenSupport = () => {
    setCurrentBotMode('support');
  };

  const handleOpenGraphingBot = () => {
    setCurrentBotMode('graphing');
  };

  const handleBotModeChange = (mode) => {
    setCurrentBotMode(mode);
  };

  const shouldShowPromptGuide = () => {
    return currentBotMode === 'knowledge' || currentBotMode === 'support';
  };

  const handleConversationCreated = (data) => {
    if (data && (data.id || data.conversation_id)) {
      setCurrentConversationId(data.id || data.conversation_id);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  // Clear conversation when on root path
  useEffect(() => {
    if (location.pathname === '/') {
      setCurrentConversation([]);
      setCurrentConversationId(null);
      setUserInput('');
      setIsTyping(false);
    }
  }, [location.pathname]);

  // Listen for clear conversation events
  useEffect(() => {
    const handleClearConversation = () => {
      setCurrentConversation([]);
      setCurrentConversationId(null);
      setUserInput('');
      setIsTyping(false);
    };

    window.addEventListener('clearConversation', handleClearConversation);
    
    // Listen for close sidebar event
    const handleCloseSidebar = () => {
      if (isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener('closeSidebar', handleCloseSidebar);
    
    return () => {
      window.removeEventListener('clearConversation', handleClearConversation);
      window.removeEventListener('closeSidebar', handleCloseSidebar);
    };
  }, [isSidebarOpen]);

  return (
    <div className="chat-app-container" style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      <Sidebar
        onNewChat={handleNewChat}
        handleOpenUserSettings={handleOpenUserSettings}
        isSidebarOpen={isSidebarOpen}
        handleToggleSidebar={handleToggleSidebar}
        onSelectConversation={handleSelectConversation}
        currentConversation={currentConversation}
        currentConversationId={currentConversationId}
      />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'row', width: '100%' }}>
        {/* Outlet renders the matched child route */}
        <Outlet context={{
          currentConversation,
          setCurrentConversation,
          userInput,
          setUserInput,
          isTyping,
          setIsTyping,
          currentConversationId,
          setCurrentConversationId,
          handleNewChat,
          handleToggleSidebar,
          handleOpenSupport,
          handleOpenGraphingBot,
          onConversationCreated: handleConversationCreated,
          handleOpenPromptGuide,
          handleStartConversation
        }} />
        {isPromptGuideOpen && shouldShowPromptGuide() && (
          <PromptGuideModal
            isPromptGuideOpen={isPromptGuideOpen}
            handleClosePromptGuide={handleClosePromptGuide}
            handleStartConversation={handleStartConversation}
            currentBotType={currentBotMode}
          />
        )}
        {isUserSettingsOpen && (
          <UserSettings
            handleCloseUserSettings={handleCloseUserSettings}
          />
        )}
      </div>
    </div>
  );
}

// Main Chat Component (for root path)
function MainChat() {
  const context = useOutletContext();
  
  return (
    <Chatconversation
      onNewChat={context.handleNewChat}
      handleToggleSidebar={context.handleToggleSidebar}
      handleOpenSupport={context.handleOpenSupport}
      handleOpenGraphingBot={context.handleOpenGraphingBot}
      currentConversation={context.currentConversation}
      setCurrentConversation={context.setCurrentConversation}
      userInput={context.userInput}
      setUserInput={context.setUserInput}
      setIsTyping={context.setIsTyping}
      onConversationCreated={context.onConversationCreated}
      currentConversationId={context.currentConversationId}
      setCurrentConversationId={context.setCurrentConversationId}
    />
  );
}

// Conversation Route Component
function ConversationRoute() {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchConversation() {
      try {
        setLoading(true);
        setError(null);
        setErrorType(null);
        
        const authToken = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        if (!authToken || !userId) {
          setErrorType('unauthorized');
          setError('Please log in to access this conversation');
          setLoading(false);
          return;
        }

        // Import the API client dynamically to avoid circular dependency
        const { apiClient } = await import('./utils/apiClient');
        
        const response = await apiClient.post(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.GET_CHAT}`, {
          conversation_id: conversationId
        });

        if (!response.ok) {
          if (response.status === 403) {
            setErrorType('forbidden');
            setError('You do not have access to this conversation');
          } else if (response.status === 404) {
            setErrorType('not_found');
            setError('This conversation does not exist');
          } else if (response.status === 401) {
            setErrorType('unauthorized');
            setError('Please log in to access this conversation');
          } else {
            setErrorType('server_error');
            setError(`Failed to load conversation (${response.status})`);
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setConversation({
          id: conversationId,
          conversation_id: conversationId,
          title: data.title || `Conversation ${conversationId}`,
          chat_history: data.chat_history || [],
          ...data,
        });
      } catch (err) {
        setErrorType('network_error');
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchConversation();
  }, [conversationId, navigate]);

  // Handle browser navigation events
  useEffect(() => {
    const handlePopState = (event) => {
      if (!window.location.pathname.startsWith('/c/')) {
        setConversation(null);
        setLoading(false);
        setError(null);
        setErrorType(null);
        window.dispatchEvent(new CustomEvent('clearConversation'));
      }
    };

    const handleClearConversation = () => {
      setConversation(null);
      setLoading(false);
      setError(null);
      setErrorType(null);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('clearConversation', handleClearConversation);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('clearConversation', handleClearConversation);
    };
  }, [navigate]);

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  const handleLogin = () => {
    navigate('/Login', { replace: true });
  };

  if (loading) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#fff', 
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <h3 style={{ color: '#333', margin: '0 0 0.5rem 0' }}>Loading conversation...</h3>
          <p style={{ color: '#666', margin: 0 }}>Please wait while we fetch your conversation</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#fff', 
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          maxWidth: '400px',
          width: '100%'
        }}>
          {/* Error Icon - Circular for forbidden, accent bar for others */}
          {errorType === 'forbidden' ? (
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: '#dc3545',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              fontSize: '2rem',
              color: 'white'
            }}>
              🚫
            </div>
          ) : (
            <div style={{
              width: '4px',
              height: '60px',
              background: errorType === 'unauthorized' ? '#007bff' : 
                         errorType === 'not_found' ? '#6c757d' : '#dc3545',
              margin: '0 auto 2rem',
              borderRadius: '2px'
            }}></div>
          )}

          {/* Error Title */}
          <h2 style={{ 
            color: '#333', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {errorType === 'unauthorized' ? 'Authentication Required' : 
             errorType === 'forbidden' ? 'Access Denied' : 
             errorType === 'not_found' ? 'Conversation Not Found' : 
             'Something Went Wrong'}
          </h2>

          {/* Error Message */}
          <p style={{ 
            color: '#666', 
            margin: '0 0 1.5rem 0',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {error}
          </p>

          {/* Additional Context */}
          {errorType === 'not_found' && (
            <p style={{ 
              color: '#888', 
              margin: '0 0 1.5rem 0',
              fontSize: '0.9rem',
              fontStyle: 'italic'
            }}>
              This conversation may have been deleted or the link might be incorrect.
            </p>
          )}

          {errorType === 'forbidden' && (
            <p style={{ 
              color: '#888', 
              margin: '0 0 1.5rem 0',
              fontSize: '0.9rem',
              fontStyle: 'italic'
            }}>
              This conversation belongs to another user or has been made private.
            </p>
          )}

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleGoHome}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#0056b3'}
              onMouseOut={(e) => e.target.style.background = '#007bff'}
            >
              Go to Home
            </button>

            {errorType === 'unauthorized' && (
              <button
                onClick={handleLogin}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#218838'}
                onMouseOut={(e) => e.target.style.background = '#28a745'}
              >
                Log In
              </button>
            )}

            {errorType === 'network_error' && (
              <button
                onClick={handleTryAgain}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#5a6268'}
                onMouseOut={(e) => e.target.style.background = '#6c757d'}
              >
                Try Again
              </button>
            )}
          </div>


        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!conversation && !loading) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minHeight: '100vh' }}>
      <ConversationView conversation={conversation} />
    </div>
  );
}

// Shared Conversation Route Component (No Authentication Required)
function SharedConversationRoute() {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    async function fetchSharedConversation() {
      try {
        setLoading(true);
        setError(null);
        setErrorType(null);

        // Import the API client dynamically to avoid circular dependency
        const { apiClient } = await import('./utils/apiClient');
        
        const response = await apiClient.get(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.SHARE}/${conversationId}`);

        if (!response.ok) {
          if (response.status === 404) {
            setErrorType('not_found');
            setError('This shared conversation does not exist');
          } else {
            setErrorType('server_error');
            setError(`Failed to load shared conversation (${response.status})`);
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setConversation({
          id: conversationId,
          conversation_id: conversationId,
          title: data.title || `Shared Conversation ${conversationId}`,
          chat_history: data.chat_history || [],
          isShared: true, // Flag to indicate this is a shared conversation
          ...data,
        });
      } catch (err) {
        setErrorType('network_error');
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchSharedConversation();
  }, [conversationId, navigate]);

  // Handle browser navigation events
  useEffect(() => {
    const handlePopState = (event) => {
      if (!window.location.pathname.startsWith('/share/')) {
        setConversation(null);
        setLoading(false);
        setError(null);
        setErrorType(null);
        window.dispatchEvent(new CustomEvent('clearConversation'));
      }
    };

    const handleClearConversation = () => {
      setConversation(null);
      setLoading(false);
      setError(null);
      setErrorType(null);
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('clearConversation', handleClearConversation);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('clearConversation', handleClearConversation);
    };
  }, [navigate]);

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleTryAgain = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#fff', 
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <h3 style={{ color: '#333', margin: '0 0 0.5rem 0' }}>Loading shared conversation...</h3>
          <p style={{ color: '#666', margin: 0 }}>Please wait while we fetch the shared conversation</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        background: '#fff', 
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem',
          maxWidth: '400px',
          width: '100%'
        }}>
          <div style={{
            width: '4px',
            height: '60px',
            background: errorType === 'not_found' ? '#6c757d' : '#dc3545',
            margin: '0 auto 2rem',
            borderRadius: '2px'
          }}></div>

          <h2 style={{ 
            color: '#333', 
            margin: '0 0 0.5rem 0',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            {errorType === 'not_found' ? 'Shared Conversation Not Found' : 'Something Went Wrong'}
          </h2>

          <p style={{ 
            color: '#666', 
            margin: '0 0 1.5rem 0',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            {error}
          </p>

          {errorType === 'not_found' && (
            <p style={{ 
              color: '#888', 
              margin: '0 0 1.5rem 0',
              fontSize: '0.9rem',
              fontStyle: 'italic'
            }}>
              This shared conversation may have been deleted or the link might be incorrect.
            </p>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={handleGoHome}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background 0.2s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#0056b3'}
              onMouseOut={(e) => e.target.style.background = '#007bff'}
            >
              Go to Home
            </button>

            {errorType === 'network_error' && (
              <button
                onClick={handleTryAgain}
                style={{
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#5a6268'}
                onMouseOut={(e) => e.target.style.background = '#6c757d'}
              >
                Try Again
              </button>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!conversation && !loading) {
    navigate('/', { replace: true });
    return null;
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', minHeight: '100vh' }}>
      <ConversationView conversation={conversation} isShared={true} />
    </div>
  );
}

function AppRoutes() {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const navigate = useNavigate();

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const handleNewChat = () => {
    window.dispatchEvent(new CustomEvent('clearConversation'));
    navigate('/');
  };

  const handleOpenUserSettings = () => {
    navigate('/UserSettings');
  };

  const handleSelectConversation = (conversation) => {
    window.dispatchEvent(new CustomEvent('navigateToConversation', {
      detail: { conversationId: conversation.id || conversation.conversation_id }
    }));
  };

  const handleOpenPromptGuide = useCallback(() => {
  }, []);

  const handleClosePromptGuide = useCallback(() => {
  }, []);

  const handlePromptSelect = useCallback((promptText) => {
  }, []);

  const handleOpenSupport = () => {
  };

  const handleOpenGraphingBot = () => {
  };

  const handleBotModeChange = (mode) => {
  };

  const handleConversationCreated = (data) => {
    if (data && (data.id || data.conversation_id)) {
      // setCurrentConversationId(data.id || data.conversation_id);
    }
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="App">
      <Routes>
        {/* Nested routes with persistent ChatLayout */}
        <Route path="/" element={
          <FreeChatGuard requireAuth={false}>
            <ChatLayout />
          </FreeChatGuard>
        }>
          {/* Main chat route */}
          <Route index element={<MainChat />} />
          {/* Conversation routes */}
          <Route path="c/:conversationId" element={<ConversationRoute />} />
          {/* Shared conversation route */}
          <Route path="share/:conversationId" element={<SharedConversationRoute />} />
        </Route>

        {/* Standalone routes */}
        <Route path="/auth/callback" element={<Login />} />
        <Route path="/Login" element={<Login />} />
        <Route path="/status" element={<Status />} />
       
        <Route path="/Icons" element={<Icons />} />
      </Routes>

      <div className="tap-top" onClick={handleScrollTop} style={{ cursor: 'pointer' }}>
        <div>
          <i className="fas fa-angle-up"></i>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
